import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import "react-table/react-table.css";
import * as api from "../../api/IrsClient";
import Modal from "../common/Modal";
import ModalError from "../common/ModalError";
import ExchangeRateModal from "./ExchangeRateModal";
import ExchangeRateSearchForm from "./ExchangeRateSearchForm";
import ExchangeRateTable from "./ExchangeRateTable";
import DismissChangeModal from "./DismissChangeModal";
import { ERROR_PREFIX_REGEX } from "../../constant";

function ExchangeRateConfiguration({ userGroup }) {
  // modal states
  const [dismissModal, setDismissModal] = useState(null);
  const [errorModal, setErrorModal] = useState(null);
  const [infoModal, setInfoModal] = useState(null);

  const [isLoading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [exchangeRatePageable, setExchangeRatePageable] = useState({});
  const [exchangeRateRequest, setExchangeRateRequest] = useState({
    is_only_enable: false,
    sort_min_trans_amount: "DESC"
  });
  const fxRole = userGroup?.includes("/services/irs/irs-exchange-rate");

  useEffect(() => {
    init();
  }, []);

  async function init() {
    getExchangeRateV4({ is_only_enable: false });
  }

  const handleOpenModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddRateModal = () => {
    setDismissModal({
      onCancel: () => {
        setIsAddModalOpen(false);
        setDismissModal(null);
      }
    })
  };

  /**
   * Handles the submission of a new exchange rate.
   *
   * @param {Object} data - The data for the new exchange rate.
   */
  const handleSubmitExchangeRate = async (data) => {
    try {
      setLoading(true);
      const { data: { status } } = await api.createUpdateExchangeRateV4(data);
      if (status.code !== "0") {
        setErrorModal({
          title: "Add the customer rate fail!",
          description: status.message.replace(ERROR_PREFIX_REGEX, "")
        });
      } else {
        setInfoModal({
          title: "Add the new customer rate successfully",
          textCancel: "OK"
        });
        setIsAddModalOpen(false);
        refreshExchangeRate();
      }
    } catch (error) {
      setErrorModal({
        title: error.response?.data?.status.code === "9000" ? "System Error" : "Customer rate update failed!",
        description: error.response?.data?.status.message.replace(ERROR_PREFIX_REGEX, "")
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles the search form submission for exchange rates.
   *
   * @param {Object} formValue - The form values.
   */
  function handleOnClickSearch(formValue) {
    const requestBody = {
      country_code: formValue.country ? formValue.country : null,
      type: formValue.rate_type ? formValue.rate_type : null,
      is_only_enable: formValue.show_only_enable_label
    }
    setExchangeRateRequest(requestBody);
    getExchangeRateV4(requestBody);
  }

  /**
   * Handles the page request change for exchange rates.
   *
   * @param {number} page - The current page number.
   * @param {number} pageSize - The number of items per page.
   */
  function handleOnPageRequestChange(page, pageSize) {
    getExchangeRateV4({
      page,
      page_size: pageSize,
      ...exchangeRateRequest
    });
  }

  function handleOnSortedChange(sortedBy) {
    const by = sortedBy[0];
    const request = { ...exchangeRateRequest };
    if (by.desc) {
      request.sort_min_trans_amount = "DESC";
    } else {
      request.sort_min_trans_amount = "ASC";
    }
    setExchangeRateRequest(request);
    getExchangeRateV4(request);
  }

  /**
   * Refreshes the exchange rate data after creating, updating, or deleting an exchange rate.
   */
  function refreshExchangeRate() {
    getExchangeRateV4(exchangeRateRequest)
  }

  async function getExchangeRateV4(request) {
    try {
      setLoading(true);
      setExchangeRatePageable({});

      const {
        data: { data, status },
      } = await api.getExchangeRateV4(request);

      if (status.code !== "0") {
        setErrorModal({
          title: "System Error",
          description: "Cannot Get Exchange Rate."
        });
      }
      setExchangeRatePageable(data);
    } catch (error) {
      setErrorModal({
        title: "System Error",
        description: "Cannot Get Exchange Rate."
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {/* Modals */}
      <Modal
        message={infoModal?.title}
        textOk={infoModal?.textOk}
        textCancel={infoModal?.textCancel}
        isOpen={!!infoModal}
        description={infoModal?.description}
        onOk={infoModal?.onOk}
        onCancel={() => setInfoModal(null)}
      />
      <ModalError
        message={errorModal?.title ?? "System Error"}
        isOpen={!!errorModal}
        description={errorModal?.description ?? ""}
        onRequestClose={() => setErrorModal(null)}
      />
      <DismissChangeModal
        isOpen={!!dismissModal}
        onOk={() => setDismissModal(null)}
        onCancel={dismissModal?.onCancel}
      />

      <div className="container">
        <div className="card card-secondary">
          <div className="card-header">
            <h5>Customer Rate</h5>
          </div>

          <ExchangeRateSearchForm
            onClickSearch={handleOnClickSearch}
            isLoading={isLoading}
          />

          <div className="col mt-3 d-flex justify-content-end">
            {!fxRole && (
              <button
                onClick={handleOpenModal}
                className="btn btn-icon btn-warning"
              >
                <FontAwesomeIcon icon={faPlus} /> Add
              </button>
            )}
          </div>
          <ExchangeRateTable
            userGroup={userGroup}
            exchangeRatePageable={exchangeRatePageable}
            onPageRequestChange={handleOnPageRequestChange}
            onExchangeRateUpdateSuccess={refreshExchangeRate}
            onSortedChange={handleOnSortedChange}
            isLoading={isLoading}
            setInfoModal={setInfoModal}
            setErrorModal={setErrorModal}
            setDismissModal={setDismissModal}
          />
        </div>
      </div>

      <ExchangeRateModal
        header="Add Customer Rate"
        isOpen={isAddModalOpen}
        loading={isLoading}
        onRequestClose={handleCloseAddRateModal}
        onOk={handleSubmitExchangeRate}
        onCancel={handleCloseAddRateModal}
        textOk="Submit"
        textCancel="Cancel"
        width={600}
        userGroup={userGroup}
        mode="ADD"
      />
    </>
  );
}

export default ExchangeRateConfiguration;